<script setup lang="ts">
import type { TableOptions } from '@core/stores/user'
import type { SortItem } from '@core/types'

const { t } = useI18n()
const table = 'ai-assistants'

definePage({
  meta: {
    action: 'read',
    subject: 'ai-assistants',
  },
})

const headers = [
  { title: t(`${table}-page.external_id`), key: 'external_id' },
  { title: t(`${table}-page.description`), key: 'description' },
  { title: t('profile.language'), key: 'language' },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const useLoading = false
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const itemForDelete = ref()
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const { data: itemsData, execute: fetchItems } = await useApi<any>(createUrl(`/${table}/`,
  {
    query: {
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
    },
  },
))

const updateList = async () => {
  isLoading.value = useLoading
  fetchItems().then(() => {
    isLoading.value = false
  })
}

const deleteItem = async () => {
  await $api(`/${table}/`, {
    method: 'DELETE',
    body: {
      id: itemForDelete.value.id,
    },
  })
  await updateList()
}

const deleteConfirmation = async (item: any) => {
  isDeleteConfirmDialogVisible.value = !isDeleteConfirmDialogVisible.value
  itemForDelete.value = item
}

const showEditDialog = async (item: any) => {
  isAddDialogOpen.value = !isAddDialogOpen.value
  currenItem.value = item
}

const items = computed(() => itemsData.value.data)

const total = computed(() => itemsData.value.total)

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            prepend-icon="tabler-plus"
            @click="isAddDialogOpen = !isAddDialogOpen"
          >
            {{ t(`${table}-page.add`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.actions="{ item }">
            <div class="d-flex align-items-end justify-end">
              <IconBtn>
                <VIcon
                  icon="tabler-edit"
                  :title="t(`${table}-page.edit`)"
                  @click="showEditDialog(item)"
                />
              </IconBtn>
              <IconBtn>
                <VIcon
                  :title="t(`${table}-page.delete`)"
                  icon="tabler-trash"
                  @click="deleteConfirmation(item)"
                />
              </IconBtn>
            </div>
          </template>
          <template #item.external_id="{ item }">
            {{ item.external_id }}
          </template>
          <template #item.description="{ item }">
            {{ item.description }}
          </template>
          <template #item.language="{ item }">
            {{ item.language }}
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>

    <AIAssistantAddEditDialog
      v-model:isDialogVisible="isAddDialogOpen"
      :ai-assistant="currenItem"
      @submit="updateList"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('confirmation.sure')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
  </div>
</template>

<style lang="scss">
.ProseMirror-focused{
  border: none;
}
</style>
